import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/**
 * Maps Swagger UI’s default chrome to the app MUI theme (light/dark, Privigo palette).
 */
export function getApiDocsSwaggerSx(theme: Theme): SxProps<Theme> {
    const { palette, typography, shape, spacing } = theme;
    const isDark = palette.mode === 'dark';
    const border = palette.divider;
    const subtleBg = isDark ? alpha(palette.common.white, 0.04) : alpha(palette.common.black, 0.03);
    const tagHover = palette.action.hover;

    return {
        position: 'relative',
        color: palette.text.primary,

        '& .swagger-ui': {
            fontFamily: typography.fontFamily,
            color: 'inherit',
            fontSize: typography.body2.fontSize
        },

        /* Title + description */
        '& .swagger-ui .information-container': {
            padding: 0,
            marginBottom: spacing(2)
        },
        '& .swagger-ui .info': {
            margin: 0
        },
        '& .swagger-ui .info .title': {
            ...typography.h5,
            fontWeight: 600,
            color: palette.text.primary,
            marginBottom: spacing(0.5)
        },
        '& .swagger-ui .info .title small': {
            display: 'none'
        },
        '& .swagger-ui .info .description': {
            ...typography.body2,
            color: palette.text.secondary,
            marginTop: spacing(0.5),
            '& p': { margin: '0 0 0.5em' },
            '& a': {
                color: palette.secondary.main,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
            }
        },
        '& .swagger-ui .info .base-url': {
            display: 'none'
        },

        /* Servers + Authorize toolbar */
        '& .swagger-ui .scheme-container': {
            margin: 0,
            padding: spacing(2),
            backgroundColor: subtleBg,
            border: `1px solid ${border}`,
            borderRadius: Number(shape.borderRadius) * 1.5,
            boxShadow: 'none'
        },
        '& .swagger-ui .scheme-container .schemes-server-container > label': {
            ...typography.subtitle2,
            color: palette.text.secondary,
            marginRight: spacing(1)
        },
        '& .swagger-ui .scheme-container select': {
            fontFamily: typography.fontFamily,
            fontSize: typography.body2.fontSize,
            padding: spacing(1, 1.5),
            borderRadius: Number(shape.borderRadius),
            border: `1px solid ${border}`,
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
            maxWidth: '100%'
        },
        '& .swagger-ui .auth-wrapper': {
            justifyContent: 'flex-end'
        },
        '& .swagger-ui .btn.authorize': {
            borderRadius: Number(shape.borderRadius),
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: palette.secondary.main,
            color: palette.secondary.main,
            backgroundColor: 'transparent',
            fontFamily: typography.fontFamily,
            fontWeight: 600,
            textTransform: 'none',
            padding: spacing(0.75, 2),
            boxShadow: 'none',
            '&:hover, &:focus': {
                backgroundColor: alpha(palette.secondary.main, isDark ? 0.18 : 0.08),
                boxShadow: 'none'
            }
        },
        '& .swagger-ui .btn.authorize svg': {
            fill: palette.secondary.main
        },
        '& .swagger-ui .btn.authorize.unlocked': {
            backgroundColor: alpha(palette.secondary.main, isDark ? 0.25 : 0.12),
            color: palette.secondary.contrastText,
            borderColor: palette.secondary.dark
        },

        /* Filter */
        '& .swagger-ui .filter-container': {
            margin: spacing(2, 0),
            padding: 0
        },
        '& .swagger-ui .filter-container .filter input, & .swagger-ui .filter input, & .swagger-ui input.operation-filter-input':
            {
                width: '100%',
                maxWidth: 420,
                padding: spacing(1.25, 1.5),
                borderRadius: Number(shape.borderRadius),
                border: `1px solid ${border}`,
                backgroundColor: palette.background.paper,
                color: palette.text.primary,
                fontFamily: typography.fontFamily,
                fontSize: typography.body2.fontSize,
                outline: 'none',
                '&:focus': {
                    borderColor: palette.primary.main,
                    boxShadow: `0 0 0 2px ${alpha(palette.primary.main, 0.2)}`
                },
                '&::placeholder': {
                    color: palette.text.disabled,
                    opacity: 1
                }
            },

        /* Use full main column; Swagger’s default wrapper caps width */
        '& .swagger-ui .wrapper': {
            padding: 0,
            maxWidth: 'none',
            width: '100%',
            boxSizing: 'border-box'
        },
        '& .swagger-ui section.block': {
            margin: 0,
            padding: 0
        },

        /* Section headers (tags) — Swagger uses h3.opblock-tag in v5 */
        '& .swagger-ui h3.opblock-tag, & .swagger-ui .opblock-tag': {
            margin: 0,
            padding: spacing(0.75, 2),
            paddingLeft: spacing(2),
            border: 'none',
            borderBottom: `1px solid ${border}`,
            borderRadius: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 44,
            transition: 'background-color 0.15s ease',
            boxShadow: `inset 3px 0 0 ${alpha(palette.secondary.main, 0.65)}`,
            '&:hover': {
                backgroundColor: tagHover
            }
        },
        '& .swagger-ui h3.opblock-tag > span, & .swagger-ui .opblock-tag > span': {
            display: 'flex',
            alignItems: 'center',
            gap: spacing(1.5),
            flex: 1,
            minWidth: 0
        },
        '& .swagger-ui h3.opblock-tag .nostyle, & .swagger-ui .opblock-tag .nostyle': {
            display: 'block',
            flex: 1,
            minWidth: 0,
            fontSize: typography.subtitle1.fontSize,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: palette.text.primary
        },
        '& .swagger-ui h3.opblock-tag small, & .swagger-ui .opblock-tag small': {
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: palette.text.secondary,
            marginLeft: spacing(1.25),
            padding: spacing(0.125, 0.75),
            borderRadius: 1,
            backgroundColor: subtleBg
        },
        '& .swagger-ui h3.opblock-tag svg, & .swagger-ui .opblock-tag svg': {
            fill: palette.text.secondary
        },

        /* Operation blocks */
        '& .swagger-ui .opblock': {
            marginBottom: spacing(1),
            borderRadius: Number(shape.borderRadius),
            border: `1px solid ${border}`,
            boxShadow: isDark ? 'none' : `0 1px 2px ${alpha(palette.common.black, 0.04)}`,
            backgroundColor: palette.background.paper,
            overflow: 'hidden'
        },
        '& .swagger-ui .opblock .opblock-summary': {
            border: 'none',
            padding: spacing(1, 1.5)
        },
        '& .swagger-ui .opblock .opblock-summary-method': {
            borderRadius: Number(shape.borderRadius) * 0.75,
            fontWeight: 700,
            fontSize: '0.7rem',
            minWidth: 52,
            padding: spacing(0.5, 1)
        },
        '& .swagger-ui .opblock.opblock-get .opblock-summary-method': {
            background: alpha(palette.secondary.main, isDark ? 0.35 : 0.15),
            color: isDark ? palette.common.white : palette.secondary.dark
        },
        '& .swagger-ui .opblock.opblock-post .opblock-summary-method': {
            background: alpha('#49cc90', isDark ? 0.35 : 0.2),
            color: isDark ? palette.common.white : '#1b5e20'
        },
        '& .swagger-ui .opblock.opblock-put .opblock-summary-method': {
            background: alpha('#fca130', isDark ? 0.35 : 0.22),
            color: isDark ? palette.common.white : '#e65100'
        },
        '& .swagger-ui .opblock.opblock-delete .opblock-summary-method': {
            background: alpha(palette.error.main, isDark ? 0.35 : 0.15),
            color: isDark ? palette.common.white : palette.error.dark
        },
        '& .swagger-ui .opblock .opblock-summary-path-description-wrapper': {
            alignItems: 'center'
        },
        '& .swagger-ui .opblock .opblock-summary-path': {
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '0.8125rem',
            color: palette.text.primary
        },
        '& .swagger-ui .opblock .opblock-description-wrapper': {
            padding: spacing(0, 1.5, 1.5),
            color: palette.text.secondary,
            fontSize: typography.body2.fontSize
        },
        '& .swagger-ui .opblock-body': {
            borderTop: `1px solid ${border}`,
            backgroundColor: isDark ? alpha(palette.common.white, 0.02) : alpha(palette.common.black, 0.015)
        },
        '& .swagger-ui .highlight-code': {
            backgroundColor: isDark ? alpha(palette.common.black, 0.35) : alpha(palette.common.black, 0.04)
        },
        '& .swagger-ui .model-box': {
            backgroundColor: subtleBg,
            borderRadius: Number(shape.borderRadius)
        },
        '& .swagger-ui table thead tr th': {
            color: palette.text.secondary,
            borderBottom: `1px solid ${border}`
        },
        '& .swagger-ui table tbody tr td': {
            borderBottom: `1px solid ${alpha(border, 0.6)}`
        },
        '& .swagger-ui .btn': {
            fontFamily: typography.fontFamily,
            textTransform: 'none',
            borderRadius: Number(shape.borderRadius)
        },
        '& .swagger-ui .btn.execute': {
            backgroundColor: palette.secondary.main,
            borderColor: palette.secondary.main,
            color: palette.secondary.contrastText,
            '&:hover': {
                backgroundColor: palette.secondary.dark,
                borderColor: palette.secondary.dark
            }
        },
        '& .swagger-ui .response-col_status': {
            fontWeight: 600
        },

        /* Models section at bottom */
        '& .swagger-ui section.models': {
            border: `1px solid ${border}`,
            borderRadius: Number(shape.borderRadius) * 1.5,
            marginTop: spacing(3),
            overflow: 'hidden'
        },
        '& .swagger-ui section.models h4': {
            ...typography.subtitle1,
            fontWeight: 600,
            padding: spacing(1.5, 2),
            margin: 0,
            backgroundColor: subtleBg,
            borderBottom: `1px solid ${border}`
        },

        '& .swagger-ui .topbar': { display: 'none' }
    };
}
